// Lab-result CSV quotes. A quote opens a field only as its first character.
// Inside it, "" is one quote character. A closing quote that is not followed
// by the delimiter or a line end ends the quoted part; the rest of that field
// is plain text, and an empty quoted part is dropped. ""7 is 7. "" is empty.
// Papaparse keeps the field open instead, so ""7 swallows every later row.
// The same rules are in backend/app/samples/csv_text.py (ADR 0025).

export class CsvParseError extends Error {
  readonly fileName: string;
  readonly line: number;

  constructor(fileName: string, line: number, problem: string) {
    super(`${fileName}: Line ${line}: ${problem}`);
    this.name = "CsvParseError";
    this.fileName = fileName;
    this.line = line;
  }
}

export interface ParsedCsv {
  headers: string[];
  rows: string[][];
  /* * set when a closing quote was not followed by the delimiter or a line end */
  note?: string | null;
}

const UNCLOSED = "unclosed quote mark. Check the file in a text editor.";
const SHORT =
  "the row count does not match the lines in the file. Check the file in a text editor.";

interface Scan {
  text: string;
  n: number;
  delimiter: string;
  i: number;
  line: number;
  lenient: boolean;
  quotedNewlines: number;
}

interface KeptRow {
  fields: string[];
  line: number;
}

function normalize(text: string): string {
  return text
    .replace(/^\uFEFF/, "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");
}

function sniffDelimiter(text: string): string {
  const nl = text.indexOf("\n");
  const first = text.slice(0, nl === -1 ? text.length : nl);
  return [",", ";", "\t"].reduce(
    (best, d) => (first.split(d).length > first.split(best).length ? d : best),
    ",",
  );
}

function readPlain(scan: Scan): string {
  const start = scan.i;
  const { text, n, delimiter } = scan;
  while (scan.i < n && text[scan.i] !== delimiter && text[scan.i] !== "\n") {
    scan.i++;
  }
  return text.slice(start, scan.i);
}

function readQuoted(scan: Scan, fileName: string): string {
  const openLine = scan.line;
  scan.i++;
  let out = "";
  let newlines = 0;
  const { text, n, delimiter } = scan;
  while (scan.i < n) {
    const ch = text[scan.i]!;
    if (ch === '"') {
      if (text[scan.i + 1] === '"') {
        out += '"';
        scan.i += 2;
        continue;
      }
      scan.i++;
      if (scan.i >= n || text[scan.i] === delimiter || text[scan.i] === "\n") {
        scan.quotedNewlines += newlines;
        return out;
      }
      // A stray opening quote that only closes on a later line merged rows.
      if (newlines > 0) throw new CsvParseError(fileName, openLine, UNCLOSED);
      // The quoted part ended. Do not keep reading through later commas.
      scan.lenient = true;
      const plain = readPlain(scan);
      scan.quotedNewlines += newlines;
      return out === "" ? plain : out + plain;
    }
    if (ch === "\n") {
      out += "\n";
      newlines++;
      scan.line++;
      scan.i++;
      continue;
    }
    out += ch;
    scan.i++;
  }
  throw new CsvParseError(fileName, openLine, UNCLOSED);
}

function readField(scan: Scan, fileName: string): string {
  if (scan.text[scan.i] === '"') return readQuoted(scan, fileName);
  return readPlain(scan);
}

function collectRows(scan: Scan, fileName: string): KeptRow[] {
  const out: KeptRow[] = [];
  const { text, n, delimiter } = scan;
  while (scan.i < n) {
    if (text[scan.i] === "\n") {
      scan.i++;
      scan.line++;
      continue;
    }
    const line = scan.line;
    const start = scan.i;
    const fields: string[] = [];
    while (scan.i < n && text[scan.i] !== "\n") {
      fields.push(readField(scan, fileName));
      if (scan.i < n && text.startsWith(delimiter, scan.i)) {
        scan.i += delimiter.length;
        if (scan.i >= n || text[scan.i] === "\n") fields.push("");
        continue;
      }
      break;
    }
    const raw = text.slice(start, scan.i);
    if (scan.i < n && text[scan.i] === "\n") {
      scan.i++;
      scan.line++;
    }
    if (raw.trim() !== "") out.push({ fields, line });
  }
  return out;
}

function columnProblem(got: number, want: number): string {
  return `this row has ${got} columns and the header has ${want}. Check the file in a text editor.`;
}

// A row whose every cell is blank (",,,") is a blank line, as before this module.
function isBlankRow(row: KeptRow): boolean {
  return row.fields.every((cell) => cell.trim() === "");
}

function assertShape(
  fileName: string,
  records: KeptRow[],
  quotedNewlines: number,
  text: string,
): KeptRow[] {
  const kept = records.filter((row) => !isBlankRow(row));
  if (!kept.length) return kept;
  const width = kept[0]!.fields.length;
  for (let r = 1; r < kept.length; r++) {
    const got = kept[r]!.fields.length;
    if (got !== width) {
      throw new CsvParseError(
        fileName,
        kept[r]!.line,
        columnProblem(got, width),
      );
    }
  }
  // Newlines inside a closed quoted field are one cell, not extra rows.
  // A larger gap means rows were merged and the column check did not see it.
  const nonEmpty = text.split("\n").filter((line) => line.trim() !== "").length;
  const data = records.length - 1;
  if (data < nonEmpty - 1 - quotedNewlines) {
    throw new CsvParseError(fileName, kept[1]?.line ?? 2, SHORT);
  }
  return kept;
}

export function parseCsv(text: string, fileName = "the file"): ParsedCsv {
  const normalized = normalize(text);
  const scan: Scan = {
    text: normalized,
    n: normalized.length,
    delimiter: sniffDelimiter(normalized),
    i: 0,
    line: 1,
    lenient: false,
    quotedNewlines: 0,
  };
  const records = assertShape(
    fileName,
    collectRows(scan, fileName),
    scan.quotedNewlines,
    normalized,
  );
  const [head, ...rest] = records;
  const rows = rest.map((row) => row.fields);
  return {
    headers: (head?.fields ?? []).map((cell) => cell.trim()),
    rows,
    note: scan.lenient
      ? `Fixed stray quote marks in ${fileName}; read ${rows.length} rows.`
      : null,
  };
}
